/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, Download, RefreshCw, Filter, TrendingUp, Users as UsersIcon, Layers } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  agent: "Agent",
  manager: "Manager",
  backoffice: "Backoffice",
  admin: "Admin",
};

const ROLE_BADGE = {
  agent: "bg-amber-100 text-amber-900 border-amber-200",
  manager: "bg-sky-100 text-sky-900 border-sky-200",
  backoffice: "bg-emerald-100 text-emerald-900 border-emerald-200",
  admin: "bg-violet-100 text-violet-900 border-violet-200",
};

function downloadCSV(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function heatColor(count, max) {
  if (!count) return "bg-slate-50 text-slate-400";
  const ratio = max > 0 ? count / max : 0;
  if (ratio > 0.66) return "bg-rose-200 text-rose-900";
  if (ratio > 0.33) return "bg-orange-200 text-orange-900";
  return "bg-amber-100 text-amber-900";
}

export default function AdminFlacSecurity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [role, setRole] = useState("all");
  const [entity, setEntity] = useState("all");

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (role !== "all") params.role = role;
      if (entity !== "all") params.entity = entity;
      const r = await axios.get(`${API}/api/admin/flac-denials`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [days, role, entity]);

  const heatMap = useMemo(() => {
    if (!data) return { days: [], roles: [], grid: {}, max: 0 };
    const allRoles = ["agent", "manager", "backoffice", "admin"];
    const days_ = data.by_day.map((d) => d.day);
    const grid = {};
    let max = 0;
    data.by_day.forEach((d) => {
      grid[d.day] = {};
      allRoles.forEach((r) => {
        const c = d.counts?.[r] || 0;
        grid[d.day][r] = c;
        if (c > max) max = c;
      });
    });
    return { days: days_, roles: allRoles, grid, max };
  }, [data]);

  return (
    <div className="space-y-6 p-6" data-testid="admin-flac-security-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-rose-700" />
            <h1 className="text-2xl font-semibold tracking-tight">Securitate FLAC — Denials</h1>
          </div>
          <p className="mt-1 text-sm text-slate-600 max-w-3xl">
            Vizualizează tentativele blocate de scriere pe câmpuri restricționate. Util pentru
            identificarea utilizatorilor care încearcă acțiuni neautorizate sau a câmpurilor
            unde regulile FLAC sunt prea agresive.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Reîncarcă
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <div className="min-w-[160px]">
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">
              <Filter className="inline h-3 w-3 mr-1" /> Interval
            </label>
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger data-testid="filter-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Ultimele 7 zile</SelectItem>
                <SelectItem value="30">Ultimele 30 zile</SelectItem>
                <SelectItem value="90">Ultimele 90 zile</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Rol</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="filter-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate rolurile</SelectItem>
                <SelectItem value="agent">Agent</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="backoffice">Backoffice</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Entitate</label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger data-testid="filter-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate</SelectItem>
                <SelectItem value="sale">Sale</SelectItem>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto text-sm text-slate-600" data-testid="results-total">
            {loading ? "Se încarcă..." : `${data?.total ?? 0} denials în intervalul selectat`}
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex h-32 items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Widget 1: Top fields */}
          <Card data-testid="widget-top-fields">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-rose-700" />
                  Top 10 câmpuri cu denials
                </CardTitle>
                <CardDescription>
                  Câmpurile pe care utilizatorii încearcă cel mai des să scrie fără permisiune.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadCSV(`flac_denials_by_field_${days}d.csv`, data.by_field)}
                disabled={!data.by_field.length}
                data-testid="export-fields-csv"
              >
                <Download className="mr-1.5 h-4 w-4" /> CSV
              </Button>
            </CardHeader>
            <CardContent>
              {data.by_field.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Niciun denial înregistrat în această perioadă.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2">#</th>
                      <th className="py-2">Câmp (field_key)</th>
                      <th className="py-2 text-right">Denials</th>
                      <th className="py-2 w-1/3">Pondere</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_field.map((f, idx) => {
                      const maxCount = data.by_field[0]?.count || 1;
                      const pct = Math.round((f.count / maxCount) * 100);
                      return (
                        <tr key={f.field_key} className="border-t border-slate-100">
                          <td className="py-1.5 text-slate-400">{idx + 1}</td>
                          <td className="py-1.5 font-mono text-xs text-slate-800">{f.field_key}</td>
                          <td className="py-1.5 text-right font-medium">{f.count}</td>
                          <td className="py-1.5">
                            <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                              <div
                                className="h-full bg-rose-400"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Widget 2: Top users */}
          <Card data-testid="widget-top-users">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <UsersIcon className="h-4 w-4 text-rose-700" />
                  Top 10 utilizatori cu denials
                </CardTitle>
                <CardDescription>
                  Utilizatorii care declanșează cele mai multe denials FLAC — potențiali „luptători cu sistemul".
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadCSV(`flac_denials_by_user_${days}d.csv`, data.by_user)}
                disabled={!data.by_user.length}
                data-testid="export-users-csv"
              >
                <Download className="mr-1.5 h-4 w-4" /> CSV
              </Button>
            </CardHeader>
            <CardContent>
              {data.by_user.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Niciun denial înregistrat în această perioadă.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2">#</th>
                      <th className="py-2">Utilizator</th>
                      <th className="py-2">Rol</th>
                      <th className="py-2 text-right">Denials</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_user.map((u, idx) => (
                      <tr key={u.user_id} className="border-t border-slate-100">
                        <td className="py-1.5 text-slate-400">{idx + 1}</td>
                        <td className="py-1.5">
                          <div className="font-medium text-slate-900">{u.name || "—"}</div>
                          <div className="text-xs text-slate-500">{u.email || u.user_id}</div>
                        </td>
                        <td className="py-1.5">
                          {u.role && (
                            <Badge className={ROLE_BADGE[u.role] || "bg-slate-100"}>
                              {ROLE_LABELS[u.role] || u.role}
                            </Badge>
                          )}
                        </td>
                        <td className="py-1.5 text-right font-medium">{u.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Widget 3: Heat map */}
          <Card data-testid="widget-heatmap">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Layers className="h-4 w-4 text-rose-700" />
                  Heat map — Denials per zi × rol
                </CardTitle>
                <CardDescription>
                  Distribuția temporală a denials. Identifică spike-uri și trenduri sezonale.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const flat = [];
                  data.by_day.forEach((d) => {
                    Object.entries(d.counts || {}).forEach(([r, c]) => {
                      if (c) flat.push({ day: d.day, role: r, count: c });
                    });
                  });
                  downloadCSV(`flac_denials_heatmap_${days}d.csv`, flat);
                }}
                disabled={!data.by_day.length}
                data-testid="export-heatmap-csv"
              >
                <Download className="mr-1.5 h-4 w-4" /> CSV
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {heatMap.days.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Nicio activitate înregistrată în această perioadă.</p>
              ) : (
                <table className="min-w-full text-xs" data-testid="heatmap-table">
                  <thead>
                    <tr>
                      <th className="py-1.5 px-2 text-left text-slate-500 font-medium">Zi</th>
                      {heatMap.roles.map((r) => (
                        <th key={r} className="py-1.5 px-2 text-center text-slate-500 font-medium min-w-[80px]">
                          {ROLE_LABELS[r] || r}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatMap.days.map((day) => (
                      <tr key={day} className="border-t border-slate-100">
                        <td className="py-1.5 px-2 font-mono text-slate-700">{day}</td>
                        {heatMap.roles.map((r) => {
                          const c = heatMap.grid[day]?.[r] || 0;
                          return (
                            <td key={r} className="py-1.5 px-2 text-center">
                              <span className={`inline-block min-w-[40px] rounded px-2 py-0.5 font-medium ${heatColor(c, heatMap.max)}`}>
                                {c || "·"}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
